// src/pages/Secretary/components/AddPatientWizard.js
//
// Hasta Ekle — sağdan açılan drawer, 3 adım (nokta + çizgi göstergesi en üstte):
//   1) Hasta Ara   — TC ile ya da uygulamadaki 6 haneli kodla arama + "nasıl çalışır"
//                    (TC'siz kaydolmuş uygulama hastası yalnız kodla bulunur, 2026-10-09)
//   2) Bilgiler    — aramanın sonucuna göre: yeni hasta formu / mevcut hasta özeti / klinik kodu
//   3) Onay        — özet + sonuçta ne olacağı, son aksiyon
import React, { useState } from "react";
import { Box, Alert, Drawer, Typography, MenuItem, alpha } from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Send as SendIcon,
  HourglassEmpty as HourglassIcon,
  CheckCircleOutline as CheckCircleIcon,
  NotificationsActive as BellIcon,
} from "@mui/icons-material";
import {
  useLookupClinicPatientLinkMutation,
  useRequestClinicPatientLinkMutation,
  useDirectAddClinicPatientLinkMutation,
  useManualCreateClinicPatientLinkMutation,
} from "../../../store";
import { useAlert } from "../../../context/AlertProvider";
import useAuth from "../../../hooks/useAuth";
import {
  DrawerHeader,
  DrawerFooter,
  FormDatePicker,
  SegmentedControl,
  FormSelect,
  FormTextField,
  PhoneInput,
  formatTRPhone,
  NameInput,
} from "../../../components/atoms";
import { DrawerField, tileInputProps, DotStepper } from "../../../components/molecules";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import useTerms from "../../../hooks/useTerms";

const stepsFor = (terms) => [{ label: `${terms.patient} Ara` }, { label: "Bilgiler" }, { label: "Onay" }];

const SEARCH_MODES = [
  { value: "tc", label: "TC ile" },
  { value: "code", label: "Uygulama kodu ile" },
];

const GENDER_OPTIONS = [
  { value: "Erkek", label: "Erkek" },
  { value: "Kadın", label: "Kadın" },
];

const initialForm = {
  name: "",
  surname: "",
  email: "",
  tellNo: "",
  gender: "",
  birthDay: "",
};

const phoneComplete = (v) => formatTRPhone(v).replace(/\D/g, "").length === 12; // 90 + 10 hane

/** Bilgi satırı: ikon çipi + başlık + açıklama (step 1 "nasıl çalışır" ve step 3 notları). */
function InfoPoint({ icon, title, children, palette }) {
  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(palette.primary, 0.1),
          color: palette.primary,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }}>{title}</Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: COLORS.slate600, lineHeight: 1.5 }}>{children}</Typography>
      </Box>
    </Box>
  );
}

export default function AddPatientWizard({ open, onClose, onSuccess }) {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { showSuccess, showError } = useAlert();
  const { palette } = useClinicTheme();
  const terms = useTerms();

  const [step, setStep] = useState(0);
  const [mode, setMode] = useState("tc"); // "tc" | "code"
  const [tcNo, setTcNo] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [lookup, setLookup] = useState(null); // { case, patient?, linkId? }
  const [linkCode, setLinkCode] = useState("");
  const [form, setForm] = useState(initialForm);

  const [doLookup, { isLoading: looking }] = useLookupClinicPatientLinkMutation();
  const [doRequest, { isLoading: requesting }] = useRequestClinicPatientLinkMutation();
  const [doDirectAdd, { isLoading: addingDirect }] = useDirectAddClinicPatientLinkMutation();
  const [doManualCreate, { isLoading: creating }] = useManualCreateClinicPatientLinkMutation();

  const reset = () => {
    setStep(0);
    setMode("tc");
    setTcNo("");
    setSearchCode("");
    setLookup(null);
    setLinkCode("");
    setForm(initialForm);
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const kind = lookup?.case;
  const byCode = mode === "code";
  // Kodla aramada bulunamayan hasta için yeni kayıt açılmaz (kod yalnız uygulamada var).
  const blocked = kind === "ALREADY_ACTIVE" || kind === "ALREADY_PENDING" || (byCode && kind === "NOT_FOUND");
  const searchReady = byCode ? /^\d{6}$/.test(searchCode) : /^\d{11}$/.test(tcNo);

  const handleSearch = async () => {
    if (!searchReady) {
      showError(byCode ? "Klinik kodu 6 haneli olmalı." : "TC kimlik numarası 11 haneli olmalı.");
      return;
    }
    try {
      const res = await doLookup(byCode ? { token, linkCode: searchCode } : { token, tcNo }).unwrap();
      setLookup(res?.data || null);
      setLinkCode(byCode ? searchCode : "");
      setStep(1);
    } catch (e) {
      showError(e?.data?.message || "Arama başarısız.");
    }
  };

  const handleRequest = async () => {
    try {
      await doRequest(byCode ? { token, linkCode } : { token, tcNo, linkCode }).unwrap();
      showSuccess(`Talep gönderildi. ${terms.patientGen} onayı bekleniyor.`);
      onSuccess?.();
      close();
    } catch (e) {
      const code = e?.data?.code || e?.data?.error;
      if (code === "INVALID_CODE") {
        showError(`Klinik kodu hatalı. ${terms.patient}dan kodu tekrar onaylayın.`);
        setStep(1);
      } else if (code === "ALREADY_PENDING_OR_ACTIVE") {
        showError(`Bu ${terms.patientLower} için zaten bekleyen veya aktif bir bağ mevcut.`);
      } else {
        showError(e?.data?.message || "Talep başarısız.");
      }
    }
  };

  const handleDirectAdd = async () => {
    try {
      await doDirectAdd({ token, patientId: lookup?.patient?.id }).unwrap();
      showSuccess(`${terms.patient} kliniğinize eklendi.`);
      onSuccess?.();
      close();
    } catch (e) {
      showError(e?.data?.message || "Ekleme başarısız.");
    }
  };

  const handleManualCreate = async () => {
    try {
      await doManualCreate({ token, patient: { ...form, tcNo } }).unwrap();
      showSuccess(`${terms.patient} oluşturuldu ve kliniğinize eklendi.`);
      onSuccess?.();
      close();
    } catch (e) {
      const code = e?.data?.code;
      if (code === "DUPLICATE_PATIENT") {
        showError("Bu TC veya e-posta ile kayıtlı kullanıcı zaten mevcut.");
      } else {
        showError(e?.data?.message || "Oluşturma başarısız.");
      }
    }
  };

  // Adım 2 → 3 için geçerlilik
  const infoValid =
    kind === "NOT_FOUND"
      ? Boolean(form.name.trim() && form.surname.trim() && form.email.trim() && phoneComplete(form.tellNo))
      : kind === "EXISTS_LOGGED_IN"
      ? /^\d{6}$/.test(linkCode)
      : kind === "EXISTS_NO_LOGIN";

  const finalAction = {
    NOT_FOUND: { run: handleManualCreate, label: "Oluştur ve Ekle", icon: <PersonAddIcon sx={{ fontSize: 15 }} />, loading: creating },
    EXISTS_NO_LOGIN: { run: handleDirectAdd, label: `${terms.patientAcc} Ekle`, icon: <PersonAddIcon sx={{ fontSize: 15 }} />, loading: addingDirect },
    EXISTS_LOGGED_IN: { run: handleRequest, label: "Talep Gönder", icon: <SendIcon sx={{ fontSize: 15 }} />, loading: requesting },
  }[kind];

  // Footer: sol buton adımda "Vazgeç", sonrasında "Geri"; sağ buton adıma göre.
  const footer = (() => {
    if (step === 0) {
      return {
        cancelLabel: "Vazgeç",
        onCancel: close,
        onSubmit: handleSearch,
        submitLabel: "Ara",
        loading: looking,
        loadingLabel: "Aranıyor...",
        disabled: !searchReady,
        icon: <SearchIcon sx={{ fontSize: 15 }} />,
      };
    }
    if (step === 1) {
      return {
        cancelLabel: "Geri",
        onCancel: () => {
          setLookup(null);
          setStep(0);
        },
        onSubmit: blocked ? undefined : () => setStep(2),
        submitLabel: "Devam",
        disabled: !infoValid,
      };
    }
    return {
      cancelLabel: "Geri",
      onCancel: () => setStep(1),
      onSubmit: finalAction?.run,
      submitLabel: finalAction?.label,
      loading: finalAction?.loading,
      loadingLabel: "Gönderiliyor...",
      icon: finalAction?.icon,
    };
  })();

  const grid = { display: "grid", rowGap: 2.5, columnGap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={close}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          bgcolor: COLORS.white,
          boxShadow: "0 8px 32px 0 rgba(60,72,100,0.15)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DrawerHeader
        icon={<PersonAddIcon />}
        title={`${terms.patient} Ekle`}
        subtitle={`${terms.patientAcc} kliniğinize ekleyin veya takip talebi gönderin`}
        onClose={close}
      />

      {/* Adım göstergesi — en üstte */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${COLORS.slate200}`, flexShrink: 0 }}>
        <DotStepper steps={stepsFor(terms)} activeStep={step} />
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: COLORS.white, flex: 1, minHeight: 0, overflowY: "auto" }}>
        {/* ── Adım 1: Hasta Ara ── */}
        {step === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SegmentedControl value={mode} onChange={setMode} options={SEARCH_MODES} ariaLabel="Arama yöntemi" fullWidth />

            {byCode ? (
              <DrawerField label="Klinik Kodu (6 hane)" fullWidth>
                <FormTextField
                  key="code"
                  value={searchCode}
                  onChange={(e) => setSearchCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchReady && !looking) handleSearch();
                  }}
                  fullWidth
                  size="small"
                  variant="standard"
                  autoFocus
                  placeholder={`${terms.patientGen} uygulamadaki Kliniğim ekranındaki kod`}
                  inputProps={{ maxLength: 6, inputMode: "numeric" }}
                  InputProps={tileInputProps(true)}
                />
              </DrawerField>
            ) : (
              <DrawerField label="TC Kimlik No" fullWidth>
                <FormTextField
                  key="tc"
                  value={tcNo}
                  onChange={(e) => setTcNo(e.target.value.replace(/\D/g, "").slice(0, 11))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchReady && !looking) handleSearch();
                  }}
                  fullWidth
                  size="small"
                  variant="standard"
                  autoFocus
                  placeholder="11 haneli TC kimlik numarası"
                  inputProps={{ maxLength: 11, inputMode: "numeric" }}
                  InputProps={tileInputProps(true)}
                />
              </DrawerField>
            )}

            <Box sx={{ bgcolor: COLORS.white, border: `1px solid ${COLORS.slate200}`, borderRadius: 2, p: 2, display: "flex", flexDirection: "column", gap: 1.75 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.slate500, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                Nasıl çalışır?
              </Typography>
              <InfoPoint palette={palette} icon={<PersonAddIcon />} title={`${terms.patient} sistemde yoksa`}>
                Bilgilerini girerek {terms.patientAccLower} oluşturursunuz; {terms.patientLower} doğrudan kliniğinize eklenir.
              </InfoPoint>
              <InfoPoint palette={palette} icon={<CheckCircleIcon />} title="Kayıtlı ama uygulamayı hiç kullanmamışsa">
                Onay gerekmeden doğrudan kliniğinize eklenir.
              </InfoPoint>
              <InfoPoint palette={palette} icon={<HourglassIcon />} title="Uygulamayı kullanıyorsa → bekleyen talep">
                {terms.patient}dan profilindeki 6 haneli klinik kodunu isteyin. Talep gönderilince {terms.patientGenLower} telefonuna bildirim gider ve
                talep listenin üstündeki <strong>Bekleyen talepler</strong> bölümünde görünür. {terms.patient} onaylayınca takip başlar ve {terms.patientLower} listeye girer.
                {terms.patient} reddederse ya da 7 gün içinde yanıtlamazsa talep kapanır. Yanıt gelmeden talebi siz de iptal edebilirsiniz.
              </InfoPoint>
              <InfoPoint palette={palette} icon={<SearchIcon />} title="TC'si yoksa → uygulama kodu ile">
                Uygulamada TC girmemiş {terms.patientLower} TC ile bulunamaz. <strong>Uygulama kodu ile</strong> seçip {terms.patientGenLower} 6 haneli kodunu
                girin; talep aynı şekilde {terms.patientGenLower} onayına gider. TC'yi bağ kurulduktan sonra {terms.patientLower} kartına ekleyebilirsiniz.
              </InfoPoint>
            </Box>
          </Box>
        )}

        {/* ── Adım 2: Bilgiler ── */}
        {step === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {byCode && kind === "NOT_FOUND" && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                Bu kodla uygulamayı kullanan bir {terms.patientLower} bulunamadı. Kodu {terms.patientLower}dan tekrar teyit edin.
              </Alert>
            )}

            {!byCode && kind === "NOT_FOUND" && (
              <>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Bu TC ile kayıtlı {terms.patientLower} yok. Yeni {terms.patientLower} oluşturup kliniğinize ekleyebilirsiniz.
                </Alert>
                <Box sx={grid}>
                  <DrawerField label="Ad *">
                    <NameInput value={form.name} onChange={(v) => setForm((p) => ({ ...p, name: v }))} fullWidth size="small" variant="standard" InputProps={tileInputProps(true)} />
                  </DrawerField>
                  <DrawerField label="Soyad *">
                    <NameInput value={form.surname} onChange={(v) => setForm((p) => ({ ...p, surname: v }))} fullWidth size="small" variant="standard" InputProps={tileInputProps(true)} />
                  </DrawerField>
                  <DrawerField label="E-posta *" fullWidth>
                    <FormTextField type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} fullWidth size="small" variant="standard" InputProps={tileInputProps(true)} />
                  </DrawerField>
                  <DrawerField label="Telefon *" fullWidth>
                    <PhoneInput value={form.tellNo} onChange={(v) => setForm((p) => ({ ...p, tellNo: v }))} fullWidth size="small" variant="standard" InputProps={tileInputProps(true)} />
                  </DrawerField>
                  <DrawerField label="Doğum Tarihi">
                    <FormDatePicker
                      variant="standard"
                      fullWidth
                      value={form.birthDay}
                      onChange={(v) => setForm((p) => ({ ...p, birthDay: v }))}
                      maxDate={new Date().toISOString().slice(0, 10)}
                    />
                  </DrawerField>
                  <DrawerField label="Cinsiyet">
                    <FormSelect
                      variant="standard"
                      value={form.gender}
                      onChange={(v) => setForm((p) => ({ ...p, gender: v }))}
                      InputProps={{ disableUnderline: true }}
                      SelectProps={{ displayEmpty: true, renderValue: (v) => v || "Seçilmedi" }}
                      sx={{ "& .MuiInputBase-root": { fontSize: TYPO.body, fontWeight: 600, color: form.gender ? COLORS.slate800 : COLORS.slate400 }, "& .MuiSelect-select": { py: 0 } }}
                    >
                      {GENDER_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </FormSelect>
                  </DrawerField>
                </Box>
              </>
            )}

            {kind === "EXISTS_NO_LOGIN" && (
              <>
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  Bu {terms.patientLower} sistemde kayıtlı (henüz uygulamayı kullanmamış). Onay gerekmeden doğrudan ekleyebilirsiniz.
                </Alert>
                <Box sx={grid}>
                  <DrawerField label="Ad Soyad" fullWidth>
                    <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }}>{lookup.patient?.fullName || "—"}</Typography>
                  </DrawerField>
                  {lookup.patient?.dateOfBirth && (
                    <DrawerField label="Doğum Tarihi" fullWidth>
                      <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }}>
                        {new Date(lookup.patient.dateOfBirth).toLocaleDateString("tr-TR")}
                      </Typography>
                    </DrawerField>
                  )}
                </Box>
              </>
            )}

            {kind === "EXISTS_LOGGED_IN" && (
              <>
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  Bu {terms.patientLower} sistemde kayıtlı ve uygulamayı kullanıyor. Takip için {terms.patientGenLower} onayı gerekir.
                </Alert>
                <DrawerField label={terms.patient} fullWidth>
                  <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }}>{lookup.patient?.maskedFullName || "—"}</Typography>
                </DrawerField>
                {!byCode && (
                <DrawerField label="Klinik Kodu (6 hane) *" fullWidth>
                  <FormTextField
                    value={linkCode}
                    onChange={(e) => setLinkCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    fullWidth
                    size="small"
                    variant="standard"
                    autoFocus
                    placeholder={`${terms.patientGen} profilindeki kod`}
                    inputProps={{ maxLength: 6, inputMode: "numeric" }}
                    InputProps={tileInputProps(true)}
                  />
                </DrawerField>
                )}
              </>
            )}

            {kind === "ALREADY_ACTIVE" && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                Bu {terms.patientLower} zaten kliniğinizde takipte. {terms.patient} listesinden ulaşabilirsiniz.
              </Alert>
            )}
            {kind === "ALREADY_PENDING" && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                Bu {terms.patientLower} için zaten bekleyen bir talebiniz var. Listenin üstündeki <strong>Bekleyen talepler</strong> butonundan takip edebilirsiniz.
              </Alert>
            )}
          </Box>
        )}

        {/* ── Adım 3: Onay ── */}
        {step === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={grid}>
              {!byCode && (
                <DrawerField label="TC Kimlik No">
                  <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }}>{tcNo}</Typography>
                </DrawerField>
              )}
              <DrawerField label={terms.patient}>
                <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }}>
                  {kind === "NOT_FOUND" ? `${form.name} ${form.surname}`.trim() : lookup?.patient?.fullName || lookup?.patient?.maskedFullName || "—"}
                </Typography>
              </DrawerField>
              {kind === "NOT_FOUND" && (
                <>
                  <DrawerField label="E-posta">
                    <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, wordBreak: "break-all" }}>{form.email}</Typography>
                  </DrawerField>
                  <DrawerField label="Telefon">
                    <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }}>{formatTRPhone(form.tellNo)}</Typography>
                  </DrawerField>
                </>
              )}
              {kind === "EXISTS_LOGGED_IN" && (
                <DrawerField label="Klinik Kodu" fullWidth>
                  <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, letterSpacing: "0.2em" }}>{linkCode}</Typography>
                </DrawerField>
              )}
            </Box>

            <Box sx={{ bgcolor: COLORS.white, border: `1px solid ${COLORS.slate200}`, borderRadius: 2, p: 2, display: "flex", flexDirection: "column", gap: 1.75 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.slate500, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                Sonrasında ne olacak?
              </Typography>
              {kind === "EXISTS_LOGGED_IN" ? (
                <>
                  <InfoPoint palette={palette} icon={<BellIcon />} title={`${terms.patientDat} bildirim gider`}>
                    “Yeni klinik talebi” bildirimi {terms.patientGenLower} telefonuna düşer.
                  </InfoPoint>
                  <InfoPoint palette={palette} icon={<HourglassIcon />} title="Talep “Bekleyen Talepler”de durur">
                    {terms.patient} onaylayana kadar {terms.patientLower} listenizde görünmez. 7 gün içinde yanıt gelmezse talep kendiliğinden kapanır;
                    yanıt gelmeden iptal de edebilirsiniz.
                  </InfoPoint>
                  <InfoPoint palette={palette} icon={<CheckCircleIcon />} title="Onaylanırsa">
                    Takip başlar ve {terms.patientLower} {terms.patientLower} listenize eklenir.
                  </InfoPoint>
                </>
              ) : (
                <InfoPoint palette={palette} icon={<CheckCircleIcon />} title={`${terms.patient} hemen eklenir`}>
                  Onay beklenmez; {terms.patientLower} kaydedilir ve {terms.patientLower} listenizde görünür.
                </InfoPoint>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <DrawerFooter
        onCancel={footer.onCancel}
        cancelLabel={footer.cancelLabel}
        onSubmit={footer.onSubmit}
        submitLabel={footer.submitLabel}
        loadingLabel={footer.loadingLabel}
        loading={footer.loading || false}
        disabled={footer.disabled || false}
        startIcon={footer.icon}
        fullWidth
      />
    </Drawer>
  );
}
